import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import type { Bootstrap, PasskeySummary, PreviewData, SearchResult, WikiPage } from "./types";

export class UnauthorizedError extends Error {}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    cache: "no-store",
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (response.status === 401) throw new UnauthorizedError("unauthorized");
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error ?? `Request failed: ${response.status}`);
  }
  return await response.json() as T;
}

export const api = {
  authStatus: () => request<{ configured: boolean; authenticated: boolean; passkeysConfigured: boolean }>("/api/auth/status"),
  login: (password: string) => request<{ ok: true }>("/api/auth/login", { method: "POST", body: JSON.stringify({ password }) }),
  passkeyLoginOptions: () => request<{ ceremonyId: string; options: PublicKeyCredentialRequestOptionsJSON }>("/api/auth/passkey/login/options"),
  verifyPasskeyLogin: (ceremonyId: string, response: AuthenticationResponseJSON) => request<{ ok: true }>("/api/auth/passkey/login/verify", {
    method: "POST",
    body: JSON.stringify({ ceremonyId, response }),
  }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  passkeys: () => request<{ passkeys: PasskeySummary[] }>("/api/auth/passkeys"),
  passkeyRegistrationOptions: () => request<{ ceremonyId: string; options: PublicKeyCredentialCreationOptionsJSON }>("/api/auth/passkey/register/options"),
  verifyPasskeyRegistration: (ceremonyId: string, label: string, response: RegistrationResponseJSON) => request<{ passkey: PasskeySummary }>("/api/auth/passkey/register/verify", {
    method: "POST",
    body: JSON.stringify({ ceremonyId, label, response }),
  }),
  removePasskey: (id: string) => request<{ ok: true }>(`/api/auth/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" }),
  bootstrap: () => request<Bootstrap>("/api/bootstrap"),
  page: (path: string) => request<WikiPage>(`/api/page?path=${encodeURIComponent(path)}`),
  search: (query: string, includeLogs: boolean) => request<{ query: string; results: SearchResult[]; head: string }>(
    `/api/search?q=${encodeURIComponent(query)}&includeLogs=${includeLogs}`,
  ),
  preview: (path: string) => request<PreviewData>(`/api/preview?path=${encodeURIComponent(path)}`),
};
